import React, { useEffect, useMemo, useState } from 'react';
import { Table, Input, Button, Breadcrumb, Tag, Space, DatePicker, Grid } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { EyeOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { souscripteurService } from '../../common/services';


const { RangePicker } = DatePicker;

const Souscripteurs = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filtres
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(undefined);
  const [siteId, setSiteId] = useState(undefined);

  // (facultatif) filtre dates UI — non pris en charge par l’API ci-dessous
  const [dateRange, setDateRange] = useState(null);

  // Pagination/tri serveur
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-createdAt'); // createdAt | -createdAt | total | -total | paidTotal | -paidTotal | remaining | -remaining

  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';
  const btnFull = { width: isMobile ? '100%' : 'auto' };

  const formatMoney = (v) =>
    (Number(v || 0)).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' FCFA';

  const fetchList = async () => {
    try {
      setLoading(true);
      const data = await souscripteurService.getList({
        page,
        limit,
        sort,
        search: search || undefined,
        status: status || undefined,
        siteId: siteId || undefined,
      });

      // L’API renvoie: { data, total, page, limit }
      setRows(data?.data || []);
      setTotal(data?.total || 0);
    } catch (e) {
      console.error('Erreur liste souscripteurs:', e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sort, search, status, siteId]);

  const columns = useMemo(
    () => [
      {
        title: 'Souscripteur',
        key: 'user',
        render: (_, r) => (
          <div>
            <div style={{ fontWeight: 600 }}>
              {r.user?.firstName} {r.user?.lastName}
            </div>
            <div style={{ color: '#888' }}>{r.user?.email || '—'}</div>
            <div style={{ color: '#888' }}>{r.user?.phone || '—'}</div>
          </div>
        ),
      },
      {
        title: 'Site',
        dataIndex: ['site', 'nom'],
        key: 'site',
      },
      {
        title: 'Lot',
        key: 'lot',
        render: (_, r) => r.lot?.intitule || '—',
      },
      {
        title: 'Fréquence',
        dataIndex: 'frequence',
        key: 'frequence',
        render: (f) => <Tag color="blue">{f}</Tag>,
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        sorter: true,
        render: formatMoney,
      },
      {
        title: 'Payé',
        dataIndex: 'paidTotal',
        key: 'paidTotal',
        align: 'right',
        sorter: true,
        render: formatMoney,
      },
      {
        title: 'Reste',
        dataIndex: 'remaining',
        key: 'remaining',
        align: 'right',
        sorter: true,
        render: (v) => <span style={{ fontWeight: 600 }}>{formatMoney(v)}</span>,
      },
      {
        title: 'Statut',
        dataIndex: 'status',
        key: 'status',
        render: (s) => {
          const color = s === 'active' ? 'green' : s === 'terminee' ? 'default' : 'red';
          const label = s === 'active' ? 'Active' : s === 'terminee' ? 'Terminée' : 'Annulée';
          return <Tag color={color}>{label}</Tag>;
        },
      },
      {
        title: 'Prochaine échéance',
        key: 'next',
        render: (_, r) =>
          r?.nextInstallment?.date
            ? dayjs(r.nextInstallment.date).format('DD/MM/YYYY')
            : '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, r) => (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/dashboard/souscripteur/${r._id}`)}
          />
        ),
      },
    ],
    [navigate]
  );

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (sorter?.columnKey) {
      const field = sorter.columnKey;
      const dir = sorter.order === 'ascend' ? '' : '-';
      // champs autorisés par l’API: createdAt, total, paidTotal, remaining
      if (['total', 'paidTotal', 'remaining'].includes(field)) {
        setSort(`${dir}${field}`);
      } else {
        setSort(`${dir}createdAt`);
      }
    }
  };

  // Export (page courante)
  const exportToExcel = () => {
    const flat = rows.map((r) => ({
      Nom: `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim(),
      Email: r.user?.email || '',
      Téléphone: r.user?.phone || '',
      Site: r.site?.nom || '',
      Lot: r.lot?.intitule || '',
      Fréquence: r.frequence || '',
      Total: r.total || 0,
      Payé: r.paidTotal || 0,
      Reste: r.remaining || 0,
      Statut: r.status || '',
      'Prochaine échéance': r?.nextInstallment?.date
        ? dayjs(r.nextInstallment.date).format('DD/MM/YYYY')
        : '',
      'Créé le': r.createdAt ? dayjs(r.createdAt).format('DD/MM/YYYY HH:mm') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(flat);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Souscripteurs');
    XLSX.writeFile(wb, 'souscripteurs.xlsx');
  };

  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'souscripteurs.csv');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Liste des souscripteurs', 14, 10);
    autoTable(doc, {
      startY: 20,
      head: [['Souscripteur', 'Site', 'Lot', 'Total', 'Payé', 'Reste', 'Statut']],
      body: rows.map((r) => [
        `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim(),
        r.site?.nom || '—',
        r.lot?.intitule || '—',
        (r.total || 0).toLocaleString('fr-FR'),
        (r.paidTotal || 0).toLocaleString('fr-FR'),
        (r.remaining || 0).toLocaleString('fr-FR'),
        r.status || '—',
      ]),
    });
    doc.save('souscripteurs.pdf');
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="row">
          <div className="col-md-12">
            <Breadcrumb
              style={{ marginBottom: 16 }}
              items={[
                { title: <Link to="/dashboard" style={{ fontWeight: 'bold' }}>🏠 ACCUEIL</Link> },
                { title: <span style={{ fontWeight: 'bold' }}>👥 SOUSCRIPTEURS</span> },
              ]}
            />

            <div className="card mb-4">
              <div className="card-body">
                {/* Filtres rapides */}
                <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Rechercher (nom, email, téléphone)"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    style={{ width: 320 }}
                    allowClear
                  />
                  <RangePicker
                    onChange={(r) => setDateRange(r)}
                    placeholder={['Date début (UI)', 'Date fin (UI)']}
                    format="DD/MM/YYYY"
                    allowClear
                  />
                </div>

                {/* Export */}
                <Space wrap size={[8, 8]} style={{ width: '100%', marginBottom: 16 }}>
                  <Button icon={<FileExcelOutlined />} onClick={exportToExcel} type="primary" size={controlSize} style={btnFull}>
                    Exporter Excel (.xlsx)
                  </Button>
                  <Button icon={<DownloadOutlined />} onClick={exportToCSV} size={controlSize} style={btnFull}>
                    Exporter CSV
                  </Button>
                  <Button icon={<FilePdfOutlined />} onClick={exportToPDF} danger size={controlSize} style={btnFull}>
                    Exporter PDF
                  </Button>
                </Space>

                <Table
                  rowKey="_id"
                  columns={columns}
                  dataSource={rows}
                  loading={loading}
                  onChange={onTableChange}
                  pagination={{
                    current: page,
                    pageSize: limit,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [5, 10, 20, 50, 100],
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Souscripteurs;
